/**
 * One-time service-worker cleanup.
 *
 * pxFlow used to ship `vite-plugin-pwa` with `registerType: 'autoUpdate'`,
 * which installed a Workbox service worker (`sw.js`) that precached the
 * app shell. That SW was never used for any real offline / installability
 * UX, but it did conflict with the explicit version-check + hard-reload
 * flow in `version-check.ts` — stale clients could be pinned to an old
 * bundle by the precache.
 *
 * We removed `vite-plugin-pwa` from the build. This function runs at app
 * boot and tears down any SW + Cache Storage left behind on returning
 * users' devices so they're not stuck on the old precache forever.
 *
 * Idempotent and safe to leave in place indefinitely: on a device that
 * never had a SW, both calls resolve to empty lists and the function is
 * a no-op.
 *
 * All errors are swallowed — SW cleanup is best-effort and must never
 * break app boot.
 */
export function unregisterLegacyServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  // Unregister any installed SW.
  try {
    void navigator.serviceWorker.getRegistrations().then((regs) => {
      for (const reg of regs) {
        void reg.unregister().catch(() => {
          // Ignore — best-effort cleanup.
        });
      }
    }).catch(() => {
      // Ignore — best-effort cleanup.
    });
  } catch {
    // Ignore — some browsers throw synchronously in private modes.
  }

  // Clear any Cache Storage entries the old SW created. Without this, a
  // surviving cache can still be read by code paths that happen to match
  // a `caches.match()` — clean slate is simpler to reason about.
  try {
    if (typeof caches !== 'undefined') {
      void caches.keys().then((keys) => {
        for (const key of keys) {
          void caches.delete(key).catch(() => {
            // Ignore — best-effort cleanup.
          });
        }
      }).catch(() => {
        // Ignore — best-effort cleanup.
      });
    }
  } catch {
    // Ignore.
  }
}
